import filter from 'lodash/filter'
import find from 'lodash/find'
// Local
import { ROLES } from '../../constant'
import {
  Role,
  User
} from '../authenticated/types'

/** *****************************************************************************
 * Provides utilities for managing user roles
 */

/**
 * The UI recognizes some roles as primary user roles. These are roles that
 * the UI knows how to act on.
 * @param role User role
 * @return True if the role is recognized as a primary user role
 */
export const isPrimaryRole = (
  role: Role
): boolean => (
  role.name === ROLES.CDI ||
  role.name === ROLES.CLINICAL_ADMIN ||
  role.name === ROLES.CODER ||
  role.name === ROLES.PHYSICIAN
)

/**
 * A user can have roles that the UI either does not recognize or does not
 * know how to act on. These are supplemental roles
 * @param role User role
 * @return True if the role is recognized as a supplemental role
 */
export const isSupplementalRole = (
  role: Role
): boolean => (
  role.name === ROLES.CDI_QA_MANAGER ||
  role.name === ROLES.CODER_QA_MANAGER
)

/**
 * @param roles List of user roles
 * @return Roles filtered to include only primary roles
 */
export const withPrimaryRoles = (
  roles: Role[]
): Role[] => filter(roles, role => isPrimaryRole(role))

/**
 * @param roles List of user roles (strings or role entities)
 * @param matchRole Role to match (string or role entity)
 * @return True if list of roles includes match
 */
export const hasRole = (
  roles: string[],
  matchRole: string
): boolean => {
  const result = find(roles, r => r === matchRole)
  return !!result
}

export const hasRoleByRoleName = (
  roles: Role[],
  matchRole: {
    name: string
  }
): boolean => {
  const result = find(roles, r => r.name === matchRole.name)
  return !!result
}

/**
 * @param userRoles List of user roles
 * @param matchRoles List of roles to match
 * @return True if user has any match role
 */
export const hasAnyRole = (
  userRoles: string[],
  matchRoles: string[]
): boolean => (
  matchRoles
    .map(role => hasRole(userRoles, role))
    .includes(true)
)

/**
 * @param roles List of user roles
 * @return True if roles include Coder QA manager
 */
export const isCoderQaManager = (
  roles: Role[]
): boolean => (
  hasRoleByRoleName(
    roles,
    {
      name: ROLES.CODER_QA_MANAGER
    }
  )
)

/**
 * @param roles List of user roles
 * @return True if roles include CDI QA manager
 */
export const isCdiQaManager = (
  roles: Role[]
): boolean => (
  hasRoleByRoleName(
    roles,
    {
      name: ROLES.CDI_QA_MANAGER
    }
  )
)

/**
 * @param roles List of user roles
 * @return True if roles include CDI 
 */
export const isCdi = (roles: Role[]) => (
  hasRoleByRoleName(
    roles,
    {
      name: ROLES.CDI
    }
  )
)

/**
 * @param roles List of user roles
 * @return True if roles include CDI_RESTRICTED
 */
export const isCdiRestricted = (roles: Role[]) => hasCdiRestricted(roles)

/**
 * @param roles List of user roles
 * @return True if roles include CDI or CDI_RESTRICTED
 */
export const isCdiOrRestricted = (roles: Role[]) => isCdi(roles) || isCdiRestricted(roles)

/**
 * @param roles List of user roles
 * @return True if roles include CDI_RESTRICTED
 */
function hasCdiRestricted(roles: Role[]): boolean {
  return hasRoleByRoleName(roles, { name: ROLES.CDI_RESTRICTED })
}

export const isCdiWorkerNow = (
  currentRole: keyof typeof ROLES
): boolean => (
  currentRole === ROLES.CDI ||
   currentRole === ROLES.CDI_RESTRICTED
)

export const isCdiQaManagerNow = (
  currentRole: keyof typeof ROLES
): boolean => (
  currentRole === ROLES.CDI_QA_MANAGER
)

/**
 * @param currentUser the logged in user
 * @return the effective role to use. CDI_RESTRICTED may only see offshore patient data 
 *         and offshore encounters
 */
export const useCdiRestricted = (
  currentUser: User
): string => {
  if (hasCdiRestricted(currentUser.roles)) {
    // you may only see offshore patients
    return ROLES.CDI_RESTRICTED
  }
  const predicate = (name: string) => name !== ROLES.CDI_RESTRICTED
  // here, anything but CDI_RESTRICTED is valid
  return currentUser.roles.map(elem => elem.name).filter(predicate)[0]
}
